import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';

let active = null;
let loading = null;
const loadDefaultThree = () => (loading ??= import('three'));

/** Claim the sole game GPU lease. Replacing a surface disposes it synchronously;
 * an older owner's release cannot release its successor. No acquisition retries. */
export function claimGameRenderer(dispose) {
  active?.dispose('replaced');
  const owner = { dispose };
  active = owner;
  return () => {
    if (active === owner) active = null;
  };
}

// A bounded original studio map puts narrow white reflections against a dark
// room. Broad ambient white would flatten the token-owned saturated marble hue.
function studioEnvironment(THREE, renderer) {
  if (!THREE.DataTexture || !THREE.PMREMGenerator) return null;
  const width = 128;
  const height = 64;
  const data = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++) {
      const u = x / width;
      const v = y / height;
      const softbox = Math.exp(-Math.pow((u - 0.27) / 0.032, 8) - Math.pow((v - 0.3) / 0.14, 8));
      const rim = Math.exp(-Math.pow((u - 0.76) / 0.014, 4) - Math.pow((v - 0.4) / 0.22, 4));
      const darkBand = Math.exp(-Math.pow((u - 0.51) / 0.12, 4));
      const value = Math.round(
        Math.min(1, (0.025 + 0.045 * (1 - v)) * (1 - 0.8 * darkBand) + 0.92 * softbox + 0.7 * rim) *
          255,
      );
      const offset = (y * width + x) * 4;
      data[offset] = data[offset + 1] = data[offset + 2] = value;
      data[offset + 3] = 255;
    }
  const texture = new THREE.DataTexture(data, width, height);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  let generator;
  try {
    generator = new THREE.PMREMGenerator(renderer);
    return generator.fromEquirectangular(texture);
  } finally {
    texture.dispose();
    generator?.dispose();
  }
}

/** Lease one optional WebGL surface after engagement. Native buttons remain the
 * input/accessibility authority; unavailable graphics leave their artwork intact.
 * Rendering follows derived state, never owns rules, points or random outcomes. */
export async function enhanceGameMaterials(
  stage,
  board,
  initialState,
  { loadThree = loadDefaultThree } = {},
) {
  const fallback = (reason) => {
    board.dataset.graphics = 'fallback';
    board.dataset.graphicsReason = reason;
    return null;
  };
  if (
    !['gem-swap', 'ball-sort'].includes(initialState.gameId) ||
    !stage.isConnected ||
    typeof ResizeObserver === 'undefined'
  )
    return fallback('unsupported');
  board.dataset.graphics = 'loading';
  delete board.dataset.graphicsReason;
  let THREE;
  try {
    THREE = await loadThree();
  } catch {
    return fallback('import');
  }
  if (!stage.isConnected) return fallback('disconnected');
  let white, ink, colours;
  try {
    const tokens = getComputedStyle(board);
    white = tokens.getPropertyValue('--game-white').trim();
    ink = tokens.getPropertyValue('--game-paper-ink').trim();
    colours = Array.from({ length: 6 }, (_, i) =>
      tokens.getPropertyValue(`--game-colour-${i}`).trim(),
    );
    // Missing theme values leave the independently usable native artwork intact.
    if (!white || !ink || colours.some((colour) => !colour)) return fallback('theme');
  } catch {
    return fallback('theme');
  }
  active?.dispose('replaced');
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'low-power',
    });
  } catch {
    return fallback('renderer');
  }
  board.dataset.graphics = 'loading';
  delete board.dataset.graphicsReason;
  const canvas = renderer.domElement;
  let scene, camera, sphere, crystal, reduced, environment;
  try {
    canvas.className = 'game-material-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    stage.append(canvas);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setClearColor(0, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Compress only highlight energy instead of clipping the clearcoat to white.
    // Capability guards retain the established lightweight renderer test/fallback.
    if (THREE.ACESFilmicToneMapping !== undefined) {
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 0.95;
    }
    scene = new THREE.Scene();
    environment = studioEnvironment(THREE, renderer);
    if (environment) scene.environment = environment.texture;
    camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 2000);
    camera.position.z = 800;
    scene.add(new THREE.HemisphereLight(white, ink, 0.6));
    const key = new THREE.DirectionalLight(white, 1.6);
    key.position.set(-200, 300, 500);
    scene.add(key);
    const rim = new THREE.DirectionalLight(white, 0.65);
    rim.position.set(200, -150, 200);
    scene.add(rim);
    sphere = new THREE.SphereGeometry(1, 32, 24);
    crystal = new THREE.OctahedronGeometry(1);
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  } catch {
    sphere?.dispose();
    crystal?.dispose();
    environment?.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    canvas.remove();
    return fallback('setup');
  }
  let state = initialState;
  let pieces = [];
  let materials = [];
  let frame = 0;
  let disposed = false;
  let releaseLease = null;
  let bounds = null;
  let visible = true;
  let resize = null;
  let observer = null;
  function clearPieces() {
    for (const mesh of pieces) scene.remove(mesh);
    for (const material of materials) material.dispose();
    pieces = [];
    materials = [];
  }
  function cancelFrame() {
    cancelAnimationFrame(frame);
    frame = 0;
  }
  function paused(reason) {
    // Closed diagnostics distinguish waiting for visibility/layout from failure.
    board.dataset.graphics = 'loading';
    board.dataset.graphicsReason = reason;
  }
  function draw(time, origin = time) {
    frame = 0;
    if (disposed) return;
    if (!stage.isConnected) return dispose('disconnected');
    if (document.hidden) return paused('hidden');
    if (!visible) return paused('offscreen');
    const t = reduced.matches ? 1 : Math.min(1, (time - origin) / 240);
    const scale = state.gameId === 'ball-sort' ? 1 : 0.88 + 0.12 * (1 - Math.pow(1 - t, 3));
    for (const piece of pieces) piece.scale.setScalar(piece.userData.size * scale);
    try {
      renderer.render(scene, camera);
      board.dataset.webgl = 'true';
      board.dataset.graphics = 'ready';
      delete board.dataset.graphicsReason;
      if (t < 1 && state.gameId !== 'ball-sort')
        frame = requestAnimationFrame((next) => draw(next, origin));
    } catch {
      dispose('render');
    }
  }
  function update(nextState) {
    try {
      rebuild(nextState);
    } catch {
      dispose('update');
    }
  }
  function rebuild(nextState) {
    state = nextState;
    if (disposed) return;
    if (!stage.isConnected) return dispose('disconnected');
    cancelFrame();
    if (document.hidden) return paused('hidden');
    if (!visible) return paused('offscreen');
    clearPieces();
    bounds = stage.getBoundingClientRect();
    if (bounds.width < 1 || bounds.height < 1) return paused('geometry');
    renderer.setSize(bounds.width, bounds.height, false);
    camera.left = -bounds.width / 2;
    camera.right = bounds.width / 2;
    camera.top = bounds.height / 2;
    camera.bottom = -bounds.height / 2;
    camera.updateProjectionMatrix();
    const materialFor = (colour) => {
      const material = new THREE.MeshPhysicalMaterial({
        color: colours[colour],
        roughness: 0.19,
        metalness: state.gameId === 'gem-swap' ? 0.14 : 0,
        clearcoat: 1,
        clearcoatRoughness: 0.055,
        envMapIntensity: 0.85,
      });
      materials.push(material);
      return material;
    };
    const add = (node, colour, marble) => {
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const mesh = new THREE.Mesh(marble ? sphere : crystal, materialFor(colour));
      mesh.position.set(
        rect.left - bounds.left + rect.width / 2 - bounds.width / 2,
        bounds.height / 2 - (rect.top - bounds.top + rect.height / 2),
        0,
      );
      mesh.userData.size = Math.min(rect.width, rect.height) * (marble ? 0.5 : 0.4);
      mesh.rotation.set(marble ? 0 : 0.32, 0.35, marble ? 0 : Math.PI / 4);
      pieces.push(mesh);
      scene.add(mesh);
    };
    for (const [index, cell] of gameView(state).cells.entries()) {
      const node = board.querySelector(`[data-cell="${index}"]`);
      if (cell.layers) {
        const layers = node?.querySelectorAll('.game-layer') ?? [];
        cell.layers.forEach((colour, i) => add(layers[i], colour, true));
      } else if (Number.isInteger(cell.colour)) add(node, cell.colour, false);
    }
    frame = requestAnimationFrame((time) => draw(time));
  }
  function dispose(reason = 'disposed') {
    if (disposed) return;
    disposed = true;
    cancelFrame();
    resize?.disconnect();
    observer?.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
    canvas.removeEventListener('webglcontextlost', lost);
    clearPieces();
    sphere.dispose();
    crystal.dispose();
    environment?.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    canvas.remove();
    delete board.dataset.webgl;
    fallback(reason);
    releaseLease?.();
  }
  const lost = (event) => {
    event.preventDefault();
    dispose('context');
  };
  const onVisibility = () => {
    if (document.hidden) {
      cancelFrame();
      paused('hidden');
    } else update(state);
  };
  try {
    resize = new ResizeObserver(() => update(state));
    resize.observe(stage);
    observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver((entries) => {
            visible = entries[0]?.isIntersecting ?? false;
            if (!visible) {
              cancelFrame();
              paused('offscreen');
            } else update(state);
          });
    observer?.observe(stage);
    canvas.addEventListener('webglcontextlost', lost);
    document.addEventListener('visibilitychange', onVisibility);
    const lease = { update, dispose };
    releaseLease = claimGameRenderer(dispose);
    update(state);
    return disposed ? null : lease;
  } catch {
    dispose('setup');
    return null;
  }
}
